/**
 * cells/ProjectPartsCell.tsx
 *
 * Renders project parts as named label chips grouped by project.
 * Labels are color-coded by labelType:
 *   energetisch = brick background
 *   veiligheid  = amber/yellow background
 *   esthetisch  = moss background
 *   algemeen    = neutral background
 *
 * Cap: 3 visible chips per row + "+N" tooltip with the rest.
 * The ⚡ glyph is dropped per spec §5 — color carries the signal.
 */
import { Link } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ProjectPartGroup } from "shared/components/maintenance/hooks/useEnergyPerformanceData";
import { formatDate, getPartEffectiveDate } from "./utils";

// Number of chips shown before "+N more" is shown
const MAX_VISIBLE_CHIPS = 3;

interface LabelChipProps {
  title: string;
  labelType: string | null;
}

function getLabelChipClass(labelType: string | null): string {
  switch (labelType) {
    case "energetisch":
      return "bg-brick-primary/15 text-brick-primary border border-brick-primary/25";
    case "veiligheid":
      return "bg-yellow-100 text-yellow-800 border border-yellow-200";
    case "esthetisch":
      return "bg-moss-primary/15 text-moss-primary border border-moss-primary/25";
    default:
      return "bg-neutral-primary/10 text-neutral-primary border border-neutral-primary/15";
  }
}

function LabelChip({ title, labelType }: LabelChipProps) {
  return (
    <span
      className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium leading-tight ${getLabelChipClass(labelType)}`}
    >
      {title}
    </span>
  );
}

export function ProjectPartsCell({
  projectPartGroups,
}: {
  projectPartGroups: ProjectPartGroup[];
}) {
  // Flatten all parts keeping project context
  const allParts = projectPartGroups.flatMap((g) =>
    g.parts.map((p) => ({
      ...p,
      projectId: g.projectId,
      projectTitle: g.projectTitle,
    })),
  );

  if (allParts.length === 0) {
    return (
      <span className="text-neutral-secondary-mid text-xs italic">
        Geen werken
      </span>
    );
  }

  const visibleParts = allParts.slice(0, MAX_VISIBLE_CHIPS);
  const hiddenParts = allParts.slice(MAX_VISIBLE_CHIPS);
  const hiddenCount = hiddenParts.length;

  // Group visible parts by project for the project link header
  const groupedVisible: Record<
    number,
    { projectTitle: string; parts: typeof visibleParts }
  > = {};
  for (const part of visibleParts) {
    if (!groupedVisible[part.projectId]) {
      groupedVisible[part.projectId] = {
        projectTitle: part.projectTitle,
        parts: [],
      };
    }
    groupedVisible[part.projectId].parts.push(part);
  }

  return (
    <div className="space-y-1.5">
      {Object.entries(groupedVisible).map(([projectId, group]) => {
        // Earliest part start date for this project — represents when work
        // (visibly to a board member) kicked off. ISO date strings sort
        // lexicographically.
        const projectDate = group.parts
          .map((p) => getPartEffectiveDate(p))
          .filter((d): d is string => Boolean(d))
          .sort()[0];

        return (
          <div key={projectId} className="space-y-0.5">
            <div className="flex flex-wrap items-baseline gap-x-1.5">
              <Link
                to={`/renovation/projects/${projectId}`}
                className="text-neutral-primary text-xs font-semibold hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {group.projectTitle}
              </Link>
              {projectDate && (
                <span className="text-neutral-secondary-mid text-[11px]">
                  · {formatDate(projectDate)}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              {group.parts.map((part) => (
                <LabelChip
                  key={part.id}
                  title={part.labelName || part.title}
                  labelType={part.labelType}
                />
              ))}
            </div>
          </div>
        );
      })}

      {hiddenCount > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-neutral-secondary-mid cursor-help text-xs underline decoration-dotted">
                +{hiddenCount} meer
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <div className="space-y-1 p-1 text-xs">
                {hiddenParts.map((part) => (
                  <div key={part.id} className="flex items-center gap-1.5">
                    <LabelChip
                      title={part.labelName || part.title}
                      labelType={part.labelType}
                    />
                    <span className="text-neutral-secondary-mid">
                      {part.projectTitle}
                    </span>
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
